// views/Profile/components/CoinsPurchasePanel.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  CheckCircle2,
  ShieldCheck,
  Sparkles,
  RefreshCw,
  Headphones,
} from 'lucide-react';
import BillingService from '../../../services/billing.service';
import usePackages from '../../../hooks/billing/usePackages';
import PackageCard from './PackageCard';
import coinsHeroBanner from '../../../assets/coins-hero-banner.png';
import './HeroBanner.css';
import './CoinsPurchasePanel.css';

// Backend doesn't report a bonus field — derive it from price-per-coin
// relative to the cheapest package (0% bonus baseline). "Featured" is
// whichever package has the single best price-per-coin, not the
// biggest one — mirrors what the numbers actually say rather than
// assuming "more coins = more bonus".
const withPricingMeta = (packages) => {
  if (!packages || packages.length === 0) return [];
  const perCoin = packages.map((pkg) => {
    const price = Number(pkg.price);
    const coins = Number(pkg.coinAmount);
    return coins > 0 && !Number.isNaN(price) ? price / coins : null;
  });
  const validRates = perCoin.filter((r) => r != null);
  if (validRates.length === 0) {
    return packages.map((pkg) => ({ pkg, bonusPercent: 0, featured: false }));
  }
  const baseRate = Math.max(...validRates); // worst $/coin = the baseline (0% bonus)
  const bestRate = Math.min(...validRates);
  return packages.map((pkg, i) => {
    const rate = perCoin[i];
    const bonusPercent =
      rate == null ? 0 : Math.round((1 - rate / baseRate) * 100);
    return {
      pkg,
      bonusPercent,
      featured: rate != null && rate === bestRate,
    };
  });
};

/**
 * CoinsPurchasePanel — the "Buy Coins" content of WalletView's coins
 * tab. Vive bajo /accounts/:id/billing?tab=coins, que es una URL real
 * y estable — Tilopay necesita a dónde volver — dentro del shell de
 * cabecera y pestañas de WalletView, no como página aparte.
 *
 * Two modes, chosen once by whether ?order= is present on mount
 * (Tilopay return trip vs. a fresh visit): confirm | pick. Picking a
 * package calls createCheckoutSession (hosted mode, the backend
 * default) and redirects the browser to the returned `checkoutUrl` —
 * same shape as SubscriptionView's tier checkout. No embedded SDK/
 * form needed for coins anymore (that path still exists server-side
 * via `checkoutMode: 'embedded'` but isn't used here).
 *
 * Props:
 *   accountId:    string | null
 */
export default function CoinsPurchasePanel({
  accountId,
}) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const orderNumber = searchParams.get('order');

  const { data: packages, loading: packagesLoading, error: packagesError } =
    usePackages({ auto: !orderNumber });

  const [buyingPackageId, setBuyingPackageId] = useState(null);
  const [checkoutError, setCheckoutError] = useState(null);

  const handleBuy = async (pkg) => {
    if (!accountId || buyingPackageId) return;
    setBuyingPackageId(pkg.id);
    setCheckoutError(null);

    const res = await BillingService.createCheckoutSession(accountId, {
      packageId: pkg.id,
      // Tilopay vuelve aquí con ?order=. La URL lleva el id de la cuenta porque
      // el pago es de una cuenta concreta y el retorno no puede depender de en
      // qué contexto esté la sesión cuando el usuario regrese.
      redirectUrl: `${window.location.origin}/a/${accountId}/billing?tab=coins`,
    });

    if (!res.success || !res.data?.checkoutUrl) {
      setCheckoutError(
        res.success
          ? { message: t('walletPage.coinsPurchase.noCheckoutUrl', 'No checkout URL returned') }
          : res
      );
      setBuyingPackageId(null);
      return;
    }

    window.location.href = res.data.checkoutUrl;
  };

  // confirm-mode state
  const [confirmStatus, setConfirmStatus] = useState('pending'); // pending | paid | failed
  const [confirmError, setConfirmError] = useState(null);

  useEffect(() => {
    if (!orderNumber || !accountId) return;
    let cancelled = false;
    (async () => {
      const res = await BillingService.confirmIntent(accountId, orderNumber);
      if (cancelled) return;
      if (res.success && res.data?.status === 'paid') {
        setConfirmStatus('paid');
      } else if (res.success) {
        // Not yet paid — the reconciliation cron will catch it within
        // ~15 min even if this immediate confirm didn't land as 'paid'.
        setConfirmStatus('failed');
      } else {
        setConfirmError(res);
        setConfirmStatus('failed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orderNumber, accountId]);

  const packagesWithMeta = useMemo(() => withPricingMeta(packages), [packages]);

  if (orderNumber) {
    return (
      <div className="coins-purchase coins-purchase--confirm">
        {confirmStatus === 'pending' && (
          <p className="coins-purchase__status">
            {t('walletPage.coinsPurchase.confirmingPayment', 'Confirming your payment…')}
          </p>
        )}
        {confirmStatus === 'paid' && (
          <div className="coins-purchase__result coins-purchase__result--success">
            <CheckCircle2 size={24} aria-hidden="true" />
            <p>
              {t(
                'walletPage.coinsPurchase.paymentConfirmed',
                'Payment confirmed — your coins have been added.'
              )}
            </p>
          </div>
        )}
        {confirmStatus === 'failed' && (
          <div className="coins-purchase__result coins-purchase__result--pending">
            <AlertCircle size={24} aria-hidden="true" />
            <p>
              {t(
                'walletPage.coinsPurchase.paymentNotConfirmed',
                "We couldn't confirm the payment immediately{{detail}}. If you completed the payment, your coins will be added within a few minutes.",
                {
                  detail: confirmError?.message ? `: ${confirmError.message}` : '.',
                }
              )}
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="coins-purchase">
      {/* Mismo banner que el de Plan: una `<img>` y no un `background-image`
          en línea, para que las dos pestañas usen la misma definición y no
          dos que hay que acordarse de emparejar. */}
      <header className="wallet-banner coins-purchase__hero">
        <img
          src={coinsHeroBanner}
          alt=""
          aria-hidden="true"
          className="wallet-banner__image"
        />
        <div className="wallet-banner__text coins-purchase__hero-text">
          <h1>{t('walletPage.coinsPurchase.heroTitle', 'Buy Coins')}</h1>
          <p>
            {t('walletPage.coinsPurchase.heroSubtitle', 'Coins power your agents.')}
            <br />
            {t(
              'walletPage.coinsPurchase.heroSubtitleLine2',
              'Use them for models, integrations, and executions.'
            )}
          </p>
        </div>
      </header>

      <h2 className="coins-purchase__section-title">
        {t('walletPage.coinsPurchase.sectionTitle', 'Choose your coin amount')}
      </h2>

      {checkoutError && (
        <div className="coins-purchase__error" role="alert">
          <AlertCircle size={20} aria-hidden="true" />
          <p>
            {checkoutError.message ||
              t('walletPage.coinsPurchase.unableToStartCheckout', 'Unable to start checkout')}
          </p>
          {checkoutError.requestId && (
            <small className="coins-purchase__request-id">
              ref: {checkoutError.requestId}
            </small>
          )}
        </div>
      )}

      {packagesLoading && !packages && (
        <p className="coins-purchase__hint">
          {t('walletPage.coinsPurchase.loadingPackages', 'Loading packages…')}
        </p>
      )}

      {packagesError && (
        <div className="coins-purchase__error" role="alert">
          <AlertCircle size={20} aria-hidden="true" />
          <p>
            {packagesError.message ||
              t('walletPage.coinsPurchase.unableToLoadPackages', 'Unable to load packages')}
          </p>
          {packagesError.requestId && (
            <small className="coins-purchase__request-id">
              ref: {packagesError.requestId}
            </small>
          )}
        </div>
      )}

      {packages && packages.length === 0 && !packagesLoading && (
        <p className="coins-purchase__hint">
          {t('walletPage.coinsPurchase.noPackages', 'No packages available right now.')}
        </p>
      )}

      {packagesWithMeta.length > 0 && (
        <div className="coins-purchase__grid">
          {packagesWithMeta.map(({ pkg, bonusPercent, featured }) => (
            <PackageCard
              key={pkg.id}
              pkg={pkg}
              bonusPercent={bonusPercent}
              featured={featured}
              onBuy={handleBuy}
              buying={buyingPackageId === pkg.id}
            />
          ))}
        </div>
      )}

      <div className="coins-purchase__trust">
        <span className="coins-purchase__trust-title">
          {t('walletPage.coinsPurchase.trustTitle', 'Secure, hassle-free checkout')}
        </span>
        <div className="coins-purchase__trust-grid">
          <div className="coins-purchase__trust-item">
            <ShieldCheck size={18} aria-hidden="true" />
            <div>
              <strong>{t('walletPage.coinsPurchase.trustSecureTitle', '100% secure payment')}</strong>
              <span>{t('walletPage.coinsPurchase.trustSecureBody', 'Your data is protected')}</span>
            </div>
          </div>
          <div className="coins-purchase__trust-item">
            <Sparkles size={18} aria-hidden="true" />
            <div>
              <strong>
                {t('walletPage.coinsPurchase.trustInstantTitle', 'Coins available instantly')}
              </strong>
              <span>{t('walletPage.coinsPurchase.trustInstantBody', 'Ready to use in seconds')}</span>
            </div>
          </div>
          <div className="coins-purchase__trust-item">
            <RefreshCw size={18} aria-hidden="true" />
            <div>
              <strong>{t('walletPage.coinsPurchase.trustNoContractsTitle', 'No contracts')}</strong>
              <span>
                {t(
                  'walletPage.coinsPurchase.trustNoContractsBody',
                  'One-time purchase, no subscription'
                )}
              </span>
            </div>
          </div>
          <div className="coins-purchase__trust-item">
            <Headphones size={18} aria-hidden="true" />
            <div>
              <strong>{t('walletPage.coinsPurchase.trustSupportTitle', '24/7 support')}</strong>
              <span>{t('walletPage.coinsPurchase.trustSupportBody', "We're here to help")}</span>
            </div>
          </div>
        </div>

        <div className="coins-purchase__accepted">
          <span>{t('walletPage.coinsPurchase.accepted', 'We accept:')}</span>
          <span className="coins-purchase__accepted-chip">VISA</span>
          <span className="coins-purchase__accepted-chip">Mastercard</span>
          <span className="coins-purchase__accepted-chip">Amex</span>
          <span className="coins-purchase__accepted-chip">PayPal</span>
          <span className="coins-purchase__accepted-more">
            {t('walletPage.coinsPurchase.acceptedMore', 'and more')}
          </span>
        </div>
      </div>
    </div>
  );
}
